import { isDate, isValid, parseISO, format } from 'date-fns';
import { DaysOfWeek } from '../constants/enums';
import {
  IAuthToken,
  AppJwtPayload,
  ConvertibleDateType,
  IApiResponse,
  IPasswordOptions,
  IPasswordValidationStatus
} from '../constants/interfaces';
import { jwtDecode } from 'jwt-decode';
import { FocusEventHandler } from 'react';

export class Helpers {
  static toDateString(date: Date | string | null): string {
    if (!date) return '';
    if (typeof date === 'string') {
      // For ISO format dates, extract just the date part
      if (date.match(/^\d{4}-\d{2}-\d{2}(T|$)/)) {
        return date.split('T')[0];
      }

      // For any partial date input during typing, just return it as is
      // This prevents validation errors while user is typing
      if (date.match(/^\d{1,4}(-\d{0,2}){0,2}$/)) {
        return date;
      }

      return date;
    }

    const d = new Date(date);
    if (isNaN(d.getTime())) return ''; // Handle invalid dates

    // Use local date parts to avoid timezone issues
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  static toDateStringFromUtc(date: Date | string | null): string {
    // Converts UTC date to local timezone, then returns YYYY-MM-DD string
    // Used for populating date input fields with UTC dates from the database
    if (!date) return '';

    if (typeof date === 'string') {
      // If it's already in YYYY-MM-DD format, we need to parse it as UTC
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Treat as UTC date (midnight UTC) and convert to local
        const utcDate = new Date(date + 'T00:00:00Z');
        const year = utcDate.getFullYear();
        const month = String(utcDate.getMonth() + 1).padStart(2, '0');
        const day = String(utcDate.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      // For ISO format dates with time, append Z if missing to treat as UTC
      if (date.match(/^\d{4}-\d{2}-\d{2}(T|\s)/)) {
        let dateStr = date.replace(' ', 'T');
        if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
          dateStr += 'Z';
        }
        const utcDate = new Date(dateStr);
        if (!isNaN(utcDate.getTime())) {
          const year = utcDate.getFullYear();
          const month = String(utcDate.getMonth() + 1).padStart(2, '0');
          const day = String(utcDate.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      }
    }

    // If it's a Date object, format it in local time
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  static readonly createApiResponse = <T>(
    statusCode: number,
    statusText: string,
    response: T | null = null
  ): IApiResponse<T> => {
    return {
      status: {
        statusCode: statusCode,
        statusDescription: statusText,
        statusDetail: ''
      },
      response: response as T,
      isSuccessStatusCode: statusCode >= 200 && statusCode <= 299,
      contentType: null,
      errors: []
    };
  };

  static readonly decodeAuthToken = (token: string | null): IAuthToken => {
    if (!token) {
      return {
        userId: null,
        userName: null,
        permissions: [],
        token: '',
        expiresAt: null,
        refreshAt: null
      };
    }

    const decoded = jwtDecode<AppJwtPayload>(token);

    let permissions: Array<string> = [];
    const permissionString = decoded.permissions;
    try {
      permissions = JSON.parse(permissionString) as string[];
    } catch {
      permissions = [];
    }

    let refreshAt: Date | null = null;
    if (decoded.exp && decoded.iat) {
      const refreshSeconds = (decoded.exp - decoded.iat) / 2;
      refreshAt = new Date(decoded.iat * 1000);
      refreshAt.setSeconds(refreshAt.getDate() + refreshSeconds);
    }

    return {
      userId: decoded.userid,
      userName: decoded.username,
      permissions: permissions,
      token: token,
      expiresAt: decoded.exp ? new Date(decoded.exp * 1000) : null,
      refreshAt: refreshAt
    };
  };

  static readonly getEnums = <T extends { [key: string]: number | string }>(
    enumType: T
  ): Array<[key: keyof T, value: T[keyof T]]> => {
    const keys = Object.keys(enumType).filter((key) => Number.isNaN(Number(key)));
    return keys.map((key) => [key, enumType[key] as T[keyof T]]);
  };

  static readonly getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return String(error);
  };

  static readonly isRunningLocal = () => {
    return window.location.hostname === 'localhost';
  };

  static readonly getBaseUrl = () => {
    const excludePorts = ['80', '443'];
    let baseUrl = `${window.location.protocol}//${window.location.hostname}`;
    if (!excludePorts.includes(window.location.port)) {
      baseUrl += `:${window.location.port}`;
    }
    return baseUrl;
  };

  static readonly getUtcDate = (date = new Date()) => {
    const utcStr = date.toISOString();
    return new Date(utcStr);
  };

  static readonly onFocusSelect: FocusEventHandler<HTMLInputElement> = (event) => {
    event.target.select();
  };

  static readonly deepCopy = <T>(obj: T) => {
    return JSON.parse(JSON.stringify(obj)) as T;
  };

  static getDistinct<T, K extends keyof T>(arr: T[], property: K): T[K][] {
    //returns a distinct list of property values from an array of objects
    return arr
      .map((item) => item[property])
      .filter((value, index, self) => self.indexOf(value) === index);
  }

  static readonly tryParseInt = (value: string, defaultValue: number | null) => {
    let result = defaultValue;
    if (!Helpers.isNullOrWhiteSpace(value) && !isNaN(parseInt(value))) {
      result = parseInt(value);
    }
    return result;
  };

  static readonly tryParseDate = (value: string, defaultValue: number) => {
    if (value !== null && value.length > 0) {
      const result = Date.parse(value);
      if (!Number.isNaN(result)) {
        return new Date(value);
      }
    }
    return defaultValue;
  };

  static readonly addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  static readonly dateWithoutTime = (date: Date) => {
    date.setHours(0, 0, 0, 0);
    return date;
  };

  static readonly daysBetween = (startDate: Date, endDate: Date) => {
    return Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  static readonly sanitizeFolderName = (name: string) => {
    return name.replace(/[/\\?%*:|"<>]/g, '');
  };

  static readonly formatCurrency = (num: number | null, defaultValue = '0', decimalPlaces = 2) => {
    if (num === null) return defaultValue;
    if (typeof num !== 'number') return num;
    return '$' + num.toFixed(decimalPlaces).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  };

  static booleanToYesNo(value: boolean | null): string {
    if (value === null) return '';
    return value ? 'Yes' : 'No';
  }

  static readonly formatTime = (seconds: number) => {
    //returns seconds formatted to HH:MM:SS
    let negativeSign = '';
    if (seconds < 0) {
      seconds *= -1;
      negativeSign = '-';
    }

    // get hours, minutes and seconds
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    // build output
    let ret = negativeSign;
    if (hrs > 0) {
      ret += `${hrs}:${mins < 10 ? '0' : ''}`;
    }

    if (mins > 0) {
      ret += `${mins}:${secs < 10 ? '0' : ''}`;
    }

    ret += secs;
    return ret;
  };

  static readonly formatTimeAsText = (seconds: number) => {
    //returns seconds formatted to X hours X minutes X seconds
    let negativeSign = '';
    if (seconds < 0) {
      seconds *= -1;
      negativeSign = '-';
    }

    // get hours, minutes and seconds
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    // build output
    let ret = negativeSign;
    if (hrs > 0) {
      ret += `${hrs} hours`;
    }

    if (mins > 0) {
      ret += ` ${mins} minute${mins === 1 ? '' : 's'}`;
    }

    ret += ` ${secs} second${secs === 1 ? '' : 's'}`;
    return ret;
  };

  static readonly hasFlag = (value: number, flag: number) => {
    if (value === 0) return flag === 0;
    return (value & flag) === flag;
  };

  static readonly convertToDate = (value: ConvertibleDateType): Date | null => {
    if (value === null || typeof value === 'undefined') return null;
    if (isDate(value)) return value;
    if (isValid(parseISO(value.toString()))) return parseISO(value.toString());
    if (isValid(new Date(value))) return new Date(value);
    return null;
  };

  static readonly toLongDate = (date: ConvertibleDateType, defaultValue = '') => {
    date = Helpers.convertToDate(date);
    if (date === null) return defaultValue;
    if (isDate(date)) {
      return format(date, 'MMMM d, yyyy');
    } else {
      return defaultValue;
    }
  };

  static readonly toShortDate = (date: ConvertibleDateType, defaultValue = '') => {
    date = Helpers.convertToDate(date);
    if (date) {
      return format(date, 'M/d/yyyy');
    } else {
      return defaultValue;
    }
  };

  static readonly toShortDateFromUtc = (date: ConvertibleDateType, defaultValue = '') => {
    // Converts UTC date string to local timezone before formatting
    // When dates are stored as UTC in the database but should display in local time,
    // we need to parse them as UTC first, then format in local time.
    // For example: UTC: 2025-12-30 00:00:47 -> EST: 2025-12-29 19:00:47 -> Display: 12/29/2025
    if (!date) return defaultValue;

    let utcDate: Date | null = null;

    if (isDate(date)) {
      utcDate = date;
    } else if (typeof date === 'string') {
      // If the string doesn't have a timezone indicator, append 'Z' to treat it as UTC
      let dateStr = date.toString().trim();
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('T')) {
        // Format: "2025-12-30 00:00:47.5805685" -> "2025-12-30T00:00:47.5805685Z"
        dateStr = dateStr.replace(' ', 'T') + 'Z';
      } else if (!dateStr.endsWith('Z') && dateStr.includes('T') && !dateStr.includes('+')) {
        // Format: "2025-12-30T00:00:47.5805685" -> "2025-12-30T00:00:47.5805685Z"
        dateStr = dateStr + 'Z';
      }

      if (isValid(parseISO(dateStr))) {
        utcDate = parseISO(dateStr);
      } else if (isValid(new Date(date))) {
        utcDate = new Date(date);
      }
    } else {
      if (isValid(new Date(date))) {
        utcDate = new Date(date);
      }
    }

    if (!utcDate) return defaultValue;

    // Now format the date in local time
    return format(utcDate, 'M/d/yyyy');
  };

  static readonly toInputFieldDate = (date: ConvertibleDateType, defaultValue = '') => {
    date = Helpers.convertToDate(date);
    if (date) {
      return format(date, 'yyyy-MM-dd');
    } else {
      return defaultValue;
    }
  };

  static readonly toShortDateTime = (date: ConvertibleDateType, defaultValue = '') => {
    date = Helpers.convertToDate(date);
    if (date) {
      return format(date, 'M/d/yyyy h:mm a');
    } else {
      return defaultValue;
    }
  };

  static readonly toShortTime = (date: ConvertibleDateType, defaultValue = '') => {
    date = Helpers.convertToDate(date);
    if (date) {
      return format(date, 'h:mm a');
    } else {
      return defaultValue;
    }
  };

  static readonly to24HourTime = (date: ConvertibleDateType, defaultValue = '') => {
    date = Helpers.convertToDate(date);
    if (date) {
      return format(date, 'HH:mm');
    } else {
      return defaultValue;
    }
  };

  static readonly dateRangeDescription = (
    startDate: ConvertibleDateType,
    endDate: ConvertibleDateType
  ) => {
    const formatString = 'M/d/yyyy h:mm aaa';
    startDate = Helpers.convertToDate(startDate);
    endDate = Helpers.convertToDate(endDate);

    if (startDate && endDate) {
      return `${format(startDate, formatString)} - ${format(endDate, formatString)}`;
    } else if (startDate && !endDate) {
      return `From ${format(startDate, formatString)} until forever`;
    } else if (!startDate && endDate) {
      return `Display until ${format(endDate, formatString)}`;
    } else {
      return 'Always';
    }
  };

  static readonly validatePassword = (
    password: string,
    confirmPassword: string,
    passwordOptions: IPasswordOptions
  ): IPasswordValidationStatus => {
    const valStatus: IPasswordValidationStatus = {
      isValid: true,
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasNumbers: /[0-9]/.test(password),
      hasSymbol: /\W|_/.test(password),
      isLongEnough: password.length >= passwordOptions.minimumLength,
      passwordsMatch: password.length > 0 && password === confirmPassword
    };

    valStatus.isValid =
      valStatus.isLongEnough &&
      valStatus.passwordsMatch &&
      (!passwordOptions.requireLowercase ||
        (passwordOptions.requireLowercase && valStatus.hasLowercase)) &&
      (!passwordOptions.requireUppercase ||
        (passwordOptions.requireUppercase && valStatus.hasUppercase)) &&
      (!passwordOptions.requireNumber || (passwordOptions.requireNumber && valStatus.hasNumbers)) &&
      (!passwordOptions.requireSymbol || (passwordOptions.requireSymbol && valStatus.hasSymbol));

    return valStatus;
  };

  static readonly isValidPhoneNumber = (phoneNumber: string) => {
    const phoneRegEx =
      /^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/gm;
    return phoneRegEx.test(phoneNumber);
  };

  static readonly isValidEmail = (emailAddress: string) => {
    const emailRegEx = /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
    return emailRegEx.test(emailAddress);
  };

  static readonly stringToArray = (value: string) => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string' && value.length > 0) {
      if (value.indexOf(',') !== -1) {
        return value.split(',');
      }
      return [value];
    }
    return [];
  };

  static readonly sleep = async (ms: number) => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, ms);
    });
  };

  static readonly isNullOrWhiteSpace = (str: string | null | undefined) => {
    return str === null || typeof str === 'undefined' || str.toString().trim() === '';
  };

  static readonly emptyToNull = (value: string) => {
    if (this.isNullOrWhiteSpace(value)) return null;
    return value;
  };

  static readonly valueOrDefault = (value: string, defaultValue: string) => {
    if (this.isNullOrWhiteSpace(value)) return defaultValue;
    return value;
  };

  static readonly getRandomNumber = (min: number, max: number) => {
    const minCeiled = Math.ceil(min);
    const maxFloored = Math.floor(max);
    return Math.floor(Math.random() * (maxFloored - minCeiled + 1) + minCeiled);
  };

  static readonly round = (value: number, precision: number) => {
    const multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  };

  static readonly getExtension = (filename: string) => {
    const ext = filename.split('.').pop() ?? '';
    return `.${ext}`;
  };

  static readonly toFileSizeText = (bytes: number, decimalPlaces = 1) => {
    if (Math.abs(bytes) < 1024) {
      return `${bytes} B`;
    }

    const units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    let u = -1;
    const r = 10 ** decimalPlaces;

    do {
      bytes /= 1024;
      ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= 1024 && u < units.length - 1);

    return bytes.toFixed(decimalPlaces) + ' ' + units[u];
  };

  static readonly getMonths = () => {
    return Array.from({ length: 12 }, (i: number) => {
      return {
        value: i + 1,
        name: new Date(0, i).toLocaleString('en-US', { month: 'long' })
      };
    });
  };

  static readonly getDaysOfTheWeek = (weekdayOnly = false) => {
    const days = [];
    type dayType = keyof typeof DaysOfWeek;
    for (const day in DaysOfWeek) {
      if (
        DaysOfWeek[day as dayType] > 0 &&
        (!weekdayOnly ||
          (weekdayOnly && DaysOfWeek[day as dayType] > 1 && DaysOfWeek[day as dayType] < 64))
      ) {
        days.push({
          name: day.charAt(0) + day.slice(1).toLowerCase(),
          shortName: day.charAt(0) + day.slice(1, 3).toLowerCase(),
          value: DaysOfWeek[day as dayType]
        });
      }
    }
    return days;
  };

  static readonly getDayOfWeekAsText = (value: number, shortName = false) => {
    type dayType = keyof typeof DaysOfWeek;
    for (const day in DaysOfWeek) {
      if (value === DaysOfWeek[day as dayType]) {
        if (shortName) {
          return day.charAt(0) + day.slice(1, 3).toLowerCase();
        } else {
          return day.charAt(0) + day.slice(1).toLowerCase();
        }
      }
    }
    return 'Unknown';
  };

  static readonly dayOfWeekFlagToText = (dayValue: number) => {
    if (dayValue === 0) return 'None';
    if (dayValue === 127) return 'every day';
    if (dayValue === 62) return 'every weekday';

    const textValues = [];
    type dayType = keyof typeof DaysOfWeek;
    for (const day in DaysOfWeek) {
      if (DaysOfWeek[day as dayType] > 0 && this.hasFlag(dayValue, DaysOfWeek[day as dayType])) {
        textValues.push(day.charAt(0) + day.slice(1).toLowerCase());
      }
    }

    if (textValues.length === 1) {
      return textValues[0];
    }
    const oxfordComma = textValues.length > 2 ? ', ' : '';
    const last = textValues.pop() ?? '';
    return `${textValues.join(', ')}${oxfordComma} and ${last}`;
  };

  static readonly scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  static readonly scrollToId = (id: string, scrollDelay = 0) => {
    setTimeout(() => {
      const headerOffset = 70;
      const el: HTMLElement | null = document.getElementById(id);
      const elementPosition: number = el?.getBoundingClientRect().top ?? 0;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }, scrollDelay);
  };

  static readonly scrollToClassName = (id: string, offset = 0, scrollDelay = 0) => {
    setTimeout(() => {
      const headerOffset = 70;
      const el: Element | null = document.getElementsByClassName(id)[0];
      const elementPosition: number = el?.getBoundingClientRect().top ?? 0;
      const offsetPosition = elementPosition + window.scrollY - headerOffset + offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }, scrollDelay);
  };

  static readonly getProperty = <T, K extends keyof T>(o: T, propertyName: K): T[K] => {
    return o[propertyName];
  };

  static readonly formatEnumName = (value: string) => {
    //converts enum to something more readable
    //ex. SAMPLE_REPORT -> Sample Report
    if (!value) return 'Undefined';
    const sections = value.toLowerCase().split('_');
    sections.forEach((section, index, source) => {
      source[index] = section.charAt(0).toUpperCase() + section.slice(1);
    });
    return sections.join(' ');
  };

  static readonly calculatePercentage = (x: number, y: number) => {
    if (y <= 0) return 0;
    return (x / y) * 100;
  };

  static readonly exportToCSV = <T>(
    data: T[],
    filename: string,
    columnMap?: { [key: string]: string }
  ) => {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Get headers from first object or use column map
    const headers = columnMap
      ? Object.keys(columnMap)
      : Object.keys(data[0] as Record<string, unknown>);

    // Create CSV content
    const csvContent = [
      // Header row
      headers.map((header) => `"${columnMap ? columnMap[header] : header}"`).join(','),
      // Data rows
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = (row as Record<string, unknown>)[header];

            // Handle null/undefined values
            if (value === null || value === undefined) {
              return '""';
            }

            // Handle dates
            if (value instanceof Date) {
              return `"${Helpers.toShortDate(value)}"`;
            }

            // Handle booleans
            if (typeof value === 'boolean') {
              return `"${Helpers.booleanToYesNo(value)}"`;
            }

            // Handle strings with quotes and commas
            const stringValue = String(value);
            if (
              stringValue.includes('"') ||
              stringValue.includes(',') ||
              stringValue.includes('\n')
            ) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }

            return `"${stringValue}"`;
          })
          .join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      URL.revokeObjectURL(url);
    }
  };
}
